import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Stack,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Refresh, Settings, Sync, Warning, CheckCircle } from '@mui/icons-material';
import { api } from '../config/API_URL';

export default function InventoryDashboard() {
  const [status, setStatus] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const [autoReconStatus, setAutoReconStatus] = useState(null);


  const [showSettings, setShowSettings] = useState(false);
  const [metrcKeys, setMetrcKeys] = useState({
    license_number: '',
    vendor_key: '',
    user_key: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statusRes, inventoryRes] = await Promise.all([
        api.get('/metrc/status'),
        api.get('/metrc/inventory')
      ]);
      setStatus(statusRes.data);
      setInventory(inventoryRes.data.packages || []);
      setLoading(false);
    } catch (err) {
      console.error('Failed to load inventory:', err);
      setLoading(false);
    }
  };


  const triggerAutoReconcile = async () => {
    try {
        const res = await api.post('/metrc/auto-reconcile');
        setAutoReconStatus(res.data);
        
        if (res.data.success) {
        alert(`✅ Auto-reconciliation complete!\nVariance: $${res.data.variance.toFixed(2)}\nTask auto-checked!`);
        } else {
        alert(`⚠️ ${res.data.reason}`);
        }
    } catch (err) {
        alert('❌ Auto-reconciliation failed');
    }
    };


  const syncNow = async () => {
    setSyncing(true);
    try {
      await api.post('/metrc/sync-now');
      await loadData();
      alert('✅ Metrc sync complete!');
    } catch (err) {
      alert('❌ Sync failed: ' + err.response?.data?.error);
    }
    setSyncing(false);
  };

  const saveMetrcKeys = async () => {
    try {
      await api.post('/metrc/org/metrc-connect', metrcKeys);
      setShowSettings(false);
      loadData();
      alert('✅ Metrc connected!');
    } catch (err) {
      alert('❌ Connection failed');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Status Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                Metrc Inventory Sync
              </Typography>
              {status?.connected ? (
                <Alert severity="success" sx={{ mb: 0 }}>
                  Connected • Last sync: {status.last_sync ? new Date(status.last_sync).toLocaleString() : 'Never'}
                </Alert>
              ) : (
                <Alert severity="warning" sx={{ mb: 0 }}>
                  Not connected to Metrc
                </Alert>
              )}
            </Box>
            <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircle />}
            onClick={triggerAutoReconcile}
            sx={{ mt: 2 }}
            >
            Test Auto-Reconciliation
            </Button>

            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<Settings />}
                onClick={() => setShowSettings(true)}
              >
                Configure
              </Button>
              <Button
                variant="contained"
                startIcon={syncing ? <CircularProgress size={20} /> : <Sync />}
                onClick={syncNow}
                disabled={!status?.connected || syncing}
              >
                {syncing ? 'Syncing...' : 'Sync Now'}
              </Button>
            </Stack>
          </Stack>

          {status?.connected && (
            <Stack direction="row" spacing={3} sx={{ mt: 2 }}>
              <Chip label={`${status.inventory_count || 0} Packages`} color="primary" />
              <Chip label={`${status.pending_transfers || 0} Pending Transfers`} color="warning" />
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Inventory Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Current Inventory
          </Typography>
          
          {inventory.length === 0 ? (
            <Alert severity="info">
              No inventory data. {status?.connected ? 'Click "Sync Now" to fetch from Metrc.' : 'Connect to Metrc first.'}
            </Alert>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Package ID</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Updated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {inventory.map((pkg) => (
                  <TableRow key={pkg.id}>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{pkg.label}</TableCell>
                    <TableCell>{pkg.product_name}</TableCell>
                    <TableCell>{pkg.quantity}</TableCell>
                    <TableCell>{pkg.unit_of_measure}</TableCell>
                    <TableCell>
                      <Chip 
                        label={pkg.package_type} 
                        size="small"
                        color={pkg.package_type === 'Active' ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{new Date(pkg.last_modified).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {autoReconStatus && (
        <Alert severity={autoReconStatus.success ? "success" : "warning"} sx={{ mt: 2 }}>
            {autoReconStatus.success ? (
            <>
                <strong>✅ Auto-Reconciled!</strong><br />
                Metrc: ${autoReconStatus.metrc_total} | POS: ${autoReconStatus.pos_total}<br />
                Variance: ${autoReconStatus.variance.toFixed(2)} ({autoReconStatus.variance_percentage.toFixed(1)}%)
            </>
            ) : (
            <>
                <strong>⚠️ Manual Review Needed</strong><br />
                {autoReconStatus.reason}
            </>
            )}
        </Alert>
        )}

      {/* Settings Dialog */}
      <Dialog open={showSettings} onClose={() => setShowSettings(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Configure Metrc Integration</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              label="License Number"
              value={metrcKeys.license_number}
              onChange={(e) => setMetrcKeys({...metrcKeys, license_number: e.target.value})}
              fullWidth
              placeholder="ABC-12345"
            />
            <TextField
              label="Vendor API Key"
              value={metrcKeys.vendor_key}
              onChange={(e) => setMetrcKeys({...metrcKeys, vendor_key: e.target.value})}
              type="password"
              fullWidth
            />
            <TextField
              label="User API Key"
              value={metrcKeys.user_key}
              onChange={(e) => setMetrcKeys({...metrcKeys, user_key: e.target.value})}
              type="password"
              fullWidth
            />
            <Alert severity="info">
              Get your API keys from Metrc: Settings → API Keys → Create New Key
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSettings(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={saveMetrcKeys}
            disabled={!metrcKeys.license_number || !metrcKeys.vendor_key || !metrcKeys.user_key}
          >
            Connect
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}